// Process command registry — the pure catalogue behind the ⌘K command palette.
// Every command maps 1:1 onto the shared `ProcessStepEditApi` (add / insert /
// delete / wrap) or the mutation journal (undo), so the palette is just the
// keyboard-driven MIRROR of the StepEditMenu — no new engine op, no separate
// capability (DESIGN-SYSTEM.md §5: keyboard mirrors the selected-object edits).
//
// Kept OUT of the palette component file so the const builder can be imported
// without a Fast-Refresh incompatible export (a value exported from a .tsx
// forces a full reload). Pure + surface-agnostic: the host injects the active
// step's nodeId + the api, so it is unit-testable with a spy api.

import type { NodeId } from "@archlang/engine";
import type { ProcessStepEditApi, WrapConstruct } from "./processStepEdit.js";
import { WRAP_CONSTRUCTS } from "./processConstructs.js";

/** A single palette entry. `run` returns true when the edit was applied (the
 *  palette closes on success); a text-carrying command instead sets `prompt`,
 *  and the palette collects a line then calls `runWithText`. */
export interface ProcessCommand {
  /** Stable id (test hook + React key). */
  readonly id: string;
  /** Human title shown + filtered on. */
  readonly title: string;
  /** Optional keyword aliases folded into the substring filter (never shown). */
  readonly keywords?: readonly string[];
  /** A destructive action — styled danger (red) in the list. */
  readonly danger?: boolean;
  /** When set, running the command first prompts for a line (placeholder =
   *  `prompt.label`), then calls `runWithText(value)`. */
  readonly prompt?: { readonly label: string };
  /** Immediate command (no text) — returns applied?. Present iff `prompt` absent. */
  readonly run?: () => boolean;
  /** Text-carrying command — receives the prompted line. Present iff `prompt` set. */
  readonly runWithText?: (value: string) => boolean;
}

/** Everything the registry needs to decide which commands apply + how to run
 *  them. `activeNodeId` is the selected step (already stringified by the host,
 *  matching `useProcessKeyboard`); null = nothing selected → only global
 *  commands surface. */
export interface ProcessCommandContext {
  readonly api: ProcessStepEditApi;
  readonly activeNodeId: string | null;
  /** Undo the last journaled edit (→ mutations.undo). */
  readonly undo: () => void;
  /** Journal depth — 0 hides "Undo last edit" (capability = presence). */
  readonly undoDepth: number;
}

const WRAP_TITLE: Record<WrapConstruct, string> = {
  if: "Wrap in if",
  each: "Wrap in each",
  try: "Wrap in try",
  parallel: "Wrap in parallel",
  reversible: "Wrap in reversible",
};

/** Build the APPLICABLE commands for the current selection/state, in list
 *  order: step commands first (only with an active step), then the global undo.
 *  Absence signals capability — a command an api can't support is simply not in
 *  the returned array (DESIGN-SYSTEM.md §5 law 3). */
export function buildProcessCommands(ctx: ProcessCommandContext): readonly ProcessCommand[] {
  const { api, activeNodeId, undo, undoDepth } = ctx;
  const cmds: ProcessCommand[] = [];
  if (!api.canEdit) return cmds;

  if (activeNodeId !== null) {
    const nid = activeNodeId as unknown as NodeId;
    cmds.push({
      id: "add-step-after",
      title: "Add step after…",
      keywords: ["insert", "new"],
      prompt: { label: "Step (e.g. A > B)" },
      runWithText: (v) => api.addStepAfter(nid, v),
    });
    cmds.push({
      id: "add-step-before",
      title: "Add step before…",
      keywords: ["insert", "new"],
      prompt: { label: "Step (e.g. A > B)" },
      runWithText: (v) => api.insertBefore(nid, v),
    });
    for (const c of WRAP_CONSTRUCTS) {
      cmds.push({
        id: `wrap-${c}`,
        title: WRAP_TITLE[c],
        keywords: ["wrap", c],
        run: () => api.wrap(nid, c),
      });
    }
    cmds.push({
      id: "delete-step",
      title: "Delete step",
      keywords: ["remove"],
      danger: true,
      run: () => api.deleteStep(nid),
    });
  }

  if (undoDepth > 0) {
    cmds.push({
      id: "undo",
      title: "Undo last edit",
      keywords: ["revert"],
      run: () => {
        undo();
        return true;
      },
    });
  }

  return cmds;
}

/** Case-insensitive substring filter over title + keywords. An empty query
 *  keeps every command (in registry order). */
export function filterProcessCommands(
  cmds: readonly ProcessCommand[],
  query: string,
): readonly ProcessCommand[] {
  const q = query.trim().toLowerCase();
  if (q === "") return cmds;
  return cmds.filter((c) => {
    if (c.title.toLowerCase().includes(q)) return true;
    return (c.keywords ?? []).some((k) => k.toLowerCase().includes(q));
  });
}
